import { z } from 'zod';
import { defaultLikertScaleResponseTypeTwo, responseTypeSchema } from './responseTypeSchema';
import { ModuleInstanceConfigurationType } from '@/apolloClient.types';

export const DefaultSelfDeterminationQuestionCategory = {
    Autonomy: 'Autonomy',
    Competence: 'Competence',
    Relatedness: 'Relatedness',
    Overall: 'Overall',
};

const defaultInstructions =
    '1. Rate how much you agree or disagree with each statement as it relates to you and your team.';

export const defaultSelfDeterminationConfiguration: SelfDeterminationConfigurationFormData = {
    moduleConfigurationType: ModuleInstanceConfigurationType.SelfDetermination,
    numberOfQuestions: 20,
    instructions: defaultInstructions,
    instructionMobile: defaultInstructions,
    reminders: 'The only right answer is the honest one, so be true to yourself in your response.',
    showDemoVideo: false,
    categories: [
        DefaultSelfDeterminationQuestionCategory.Autonomy,
        DefaultSelfDeterminationQuestionCategory.Competence,
        DefaultSelfDeterminationQuestionCategory.Relatedness,
        DefaultSelfDeterminationQuestionCategory.Overall,
    ],
    questions: [
        {
            category: DefaultSelfDeterminationQuestionCategory.Autonomy,
            questions: [
                'I feel that my opinions are valued in my team.',
                'I have the freedom to make decisions about how to approach my tasks.',
                'I am encouraged to express my thoughts and ideas within the team.',
                'I can choose my level of involvement in team activities.',
                'I feel empowered to take initiative when necessary.',
            ],
            scaleResponseType: { likertScale: defaultLikertScaleResponseTypeTwo },
        },
        {
            category: DefaultSelfDeterminationQuestionCategory.Competence,
            questions: [
                'I believe that my skills are effectively utilized within the team.',
                'I receive constructive feedback that helps me improve my performance.',
                'I feel confident in my abilities to complete the challenges we face.',
                'My team provides opportunities for me to develop new skills.',
                'I am recognized for my contributions to the team’s success.',
            ],
            scaleResponseType: { likertScale: defaultLikertScaleResponseTypeTwo },
        },
        {
            category: DefaultSelfDeterminationQuestionCategory.Relatedness,
            questions: [
                'I have strong connections with my teammates.',
                'I feel supported by my team members during challenging times.',
                'My team fosters a sense of belonging and camaraderie.',
                'I believe that my team cares about my well-being.',
                'I enjoy collaborating with my teammates on missions and tasks.',
            ],
            scaleResponseType: { likertScale: defaultLikertScaleResponseTypeTwo },
        },
        {
            category: DefaultSelfDeterminationQuestionCategory.Overall,
            questions: [
                'Being part of this team is important to me.',
                'I am driven to work hard for the success of my team.',
                'I feel a sense of purpose and meaning in my work with my team.',
                'I feel a strong sense of commitment to my team’s goals.',
                'I take pride in being a member of this special forces unit.',
                'I believe that my contributions make a meaningful difference to the team.',
            ],
            scaleResponseType: { likertScale: defaultLikertScaleResponseTypeTwo },
        },
    ],
};

export const selfDeterminationQuestionSchema = z.object({
    category: z.string(),
    instructions: z.string().optional(),
    scaleResponseType: responseTypeSchema,
    questions: z.array(z.string()).min(1, { message: 'At least one question is required' }),
});

export const selfDeterminationConfigurationFormSchema = z
    .object({
        moduleConfigurationType: z.nativeEnum(ModuleInstanceConfigurationType),
        numberOfQuestions: z.number().optional(),
        instructions: z.string().optional(),
        instructionMobile: z.string().optional(),
        reminders: z.string().optional(),
        showDemoVideo: z.boolean().optional().nullable(),
        categories: z.array(z.string()).min(1, { message: 'At least one category is required' }),
        questions: z.array(selfDeterminationQuestionSchema),
    })
    .optional();

export type SelfDeterminationConfigurationFormData = z.infer<typeof selfDeterminationConfigurationFormSchema>;
